import { consoleLogger } from '../constants/console-logger';
import { AudioFormat } from '../device-api/audio-format';
import { VideoColourSpace, VideoFormat } from '../device-api/video-format';
import { RESOLUTIONS, supportsUhd } from './resolutions';
export var UhdHdrCodec;
(function (UhdHdrCodec) {
    UhdHdrCodec["DolbyVision"] = "DolbyVision";
    UhdHdrCodec["Hdr10"] = "Hdr10";
    UhdHdrCodec["Hdr10Plus"] = "Hdr10Plus";
    UhdHdrCodec["HdrHlg"] = "HdrHlg";
    UhdHdrCodec["Uhd"] = "Uhd";
})(UhdHdrCodec || (UhdHdrCodec = {}));
export var DrmKind;
(function (DrmKind) {
    DrmKind["FairPlay"] = "com.apple.fps";
    DrmKind["PlayReady"] = "com.microsoft.playready.recommendation";
    DrmKind["Widevine"] = "com.widevine.alpha";
})(DrmKind || (DrmKind = {}));
// DRM levels for UHD codecs
const DRM_UHD_LEVEL = {
    ["com.widevine.alpha" /* DrmKind.Widevine */]: 'HW_SECURE_ALL',
    ["com.microsoft.playready.recommendation" /* DrmKind.PlayReady */]: '3000',
};
export const DEFAULT_MIME_TYPE = 'video/mp4';
const MIN_4K_HDCP_VERSION = '2.2';
// 4k + HDR Codecs used by OVP
export const VIDEO_CODEC_CONFIGS = {
    [UhdHdrCodec.Uhd]: {
        codecs: 'hvc1.1.6.L150.90',
        contentType: 'video/mp4;codecs="hvc1.1.6.L150.90"', // H.265 Main profile, level 5 (up to 4k). 8 bits per color per pixel (SDR)
    },
    [UhdHdrCodec.HdrHlg]: {
        codecs: 'hvc1.1.6.L150.90',
        contentType: 'video/mp4;codecs="hvc1.1.6.L150.90"',
    },
    [UhdHdrCodec.Hdr10]: {
        codecs: 'hvc1.2.4.L150.90',
        contentType: 'video/mp4;codecs="hvc1.2.4.L150.90"',
        hdrTransferFunc: 'eotf="smpte2084"', // electro-optical transfer function that describes HDR color space (for use with 10-bit profiles only)
    },
    [UhdHdrCodec.Hdr10Plus]: {
        codecs: 'hvc1.2.4.L150.90',
        contentType: 'video/mp4;codecs="hvc1.2.4.L150.90"',
        hdrTransferFunc: 'eotf="smpte2084"', // electro-optical transfer function that describes HDR color space (for use with 10-bit profiles only)
    },
    [UhdHdrCodec.DolbyVision]: {
        codecs: 'dvh1.05.06',
        contentType: 'video/mp4;codecs="dvh1.05.06"',
    },
};
export var AudioCodecType;
(function (AudioCodecType) {
    AudioCodecType["DolbyDigital"] = "DolbyDigital";
    AudioCodecType["DolbyDigitalPlus"] = "DolbyDigitalPlus";
    AudioCodecType["MPEG_4_LC"] = "MPEG_4_LC";
    AudioCodecType["MPEG_4_SBR"] = "MPEG_4_SBR";
})(AudioCodecType || (AudioCodecType = {}));
export const AUDIO_CODEC_CONFIGS = {
    [AudioCodecType.MPEG_4_LC]: {
        contentType: `audio/mp4;codecs="${"mp4a.40.2" /* AudioCodec.MP4A2 */}"`,
        codecs: "mp4a.40.2" /* AudioCodec.MP4A2 */,
    },
    [AudioCodecType.MPEG_4_SBR]: {
        contentType: `audio/mp4;codecs="${"mp4a.40.5" /* AudioCodec.MP4A5 */}"`,
        codecs: "mp4a.40.5" /* AudioCodec.MP4A5 */,
    },
    [AudioCodecType.DolbyDigital]: {
        contentType: `audio/mp4;codecs="${"ac-3" /* AudioCodec.AC3 */}"`,
        codecs: "ac-3" /* AudioCodec.AC3 */,
    },
    [AudioCodecType.DolbyDigitalPlus]: {
        contentType: `audio/mp4;codecs="${"ec-3" /* AudioCodec.EC3 */}"`,
        codecs: "ec-3" /* AudioCodec.EC3 */,
    },
};
const uhdStreamSpecs = { bitrate: 12500000, width: RESOLUTIONS.UHD.minWidth, height: RESOLUTIONS.UHD.minHeight, framerate: 30 };
function supportsCodec(codec) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return 'MediaSource' in self && self.MediaSource.isTypeSupported && self.MediaSource.isTypeSupported(codec);
}
export function supportsVideoCodec(codec, eotf) {
    const codecConfig = VIDEO_CODEC_CONFIGS[codec];
    let codecString = codecConfig.contentType;
    if (eotf && codecConfig.hdrTransferFunc) {
        codecString += `;${codecConfig.hdrTransferFunc}`;
    }
    return supportsCodec(codecString);
}
export function supportsAudioCodec(codec) {
    return supportsCodec(AUDIO_CODEC_CONFIGS[codec].contentType);
}
/**
 * Probe video support for codec via `navigator.mediaCapabilities`
 */
export async function probeVideoSupportByMediaCapabilities(codecType) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (navigator.mediaCapabilities && navigator.mediaCapabilities.decodingInfo) {
        const config = {
            type: 'media-source',
            video: Object.assign({}, uhdStreamSpecs, VIDEO_CODEC_CONFIGS[codecType]),
        };
        try {
            return (await navigator.mediaCapabilities.decodingInfo(config)).supported;
        }
        catch (e) {
            consoleLogger.warn(`mediaCapabilities.decodingInfo() failed: ${e}`);
        }
    }
    return false;
}
/**
 * Probes the device for support of HDR codecs via `MediaSource.isTypeSupported`
 *
 * @returns Array of supported video colour spaces
 */
export function getSupportedColourSpaces() {
    const colourSpaces = [VideoColourSpace.SDR];
    if (supportsVideoCodec(UhdHdrCodec.HdrHlg)) {
        colourSpaces.push(VideoColourSpace.HDRHLG);
    }
    if (supportsVideoCodec(UhdHdrCodec.Hdr10)) {
        colourSpaces.push(VideoColourSpace.HDR10);
    }
    if (supportsVideoCodec(UhdHdrCodec.DolbyVision)) {
        colourSpaces.push(VideoColourSpace.DV);
    }
    return colourSpaces;
}
export function getMediaSourceSupportedAudioFormats() {
    const audioFormats = [AudioFormat.Stereo];
    if (supportsAudioCodec(AudioCodecType.DolbyDigitalPlus)) {
        audioFormats.push(AudioFormat.Dolby51, AudioFormat.Dolby71, AudioFormat.DolbyAtmos);
    }
    else if (supportsAudioCodec(AudioCodecType.DolbyDigital)) {
        audioFormats.push(AudioFormat.Dolby51);
    }
    return audioFormats;
}
/**
 * Probes device for UHD-required HDCP 2.2 support of given codec
 *
 * @param codec: codec to check
 * @param drm: flavor of DRM (required for Media Keys API)
 * @returns true if the given/drm is supported. Otherwise, returns false
 */
export async function supportsHdcp(codec, drm) {
    if ('requestMediaKeySystemAccess' in navigator) {
        try {
            const mediaKeySystemAccess = await navigator.requestMediaKeySystemAccess(drm, [
                {
                    initDataTypes: ['cenc'],
                    videoCapabilities: [
                        {
                            contentType: VIDEO_CODEC_CONFIGS[codec].contentType,
                            robustness: DRM_UHD_LEVEL[drm],
                            width: RESOLUTIONS.UHD.minWidth,
                            height: RESOLUTIONS.UHD.minHeight,
                        },
                    ],
                },
            ]);
            const mediaKeys = await mediaKeySystemAccess.createMediaKeys();
            if ('getStatusForPolicy' in mediaKeys) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const status = await mediaKeys.getStatusForPolicy({ minHdcpVersion: MIN_4K_HDCP_VERSION });
                return status === 'usable';
            }
        }
        catch {
            // Something went wrong. Let's proceed anyway
        }
    }
    return false;
}
export function getMaxVideoFormat() {
    const { availWidth, availHeight } = self.screen;
    if (supportsVideoCodec(UhdHdrCodec.Uhd) && supportsUhd({ width: availWidth, height: availHeight })) {
        return VideoFormat.UHD;
    }
    return VideoFormat.HD;
}
//# sourceMappingURL=probe-utils.js.map